import { RefObject, useEffect, useState } from 'react'
import {
  Cookies,
  datalayerSimpleEvent,
  eventClick,
  getAddress,
  getCurrentDomain,
  ISafetyValidationProps,
  isValidArrayWithData,
  postCoordsValidate,
  postNewAddress,
  updateSalesChannel,
  useContentful,
  useEvents,
  useLocalAddress,
  useMobile,
  useOrderForm,
  useSession,
  useTrigerEvent,
  useUpdateAddress
} from '@smuchile/pkg-unimarc-hooks'
import { getGlobalStyle } from '@smuchile/pkg-unimarc-components/helpers'
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng
} from 'use-places-autocomplete'
import {
  StoreLocatorV2,
  ButtonStatus,
  TitleProps
} from '@smuchile/pkg-unimarc-components'
import { Map } from './Map'
import { addressFormat } from 'shared/utils/addressFormated/address'
import { NewAddressData } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewAddressData'
import { regions } from '@smuchile/pkg-unimarc-hooks/utils/info'
import { useRouter } from 'next/router'
import { ModalConfirmChangeAddress } from 'components/ModalConfirmChangeAddress'
import { ADDRESS_ICONS } from './helpers/constants'

interface AddressMaps {
  city: string
  country: string
  neighborhood: string
  number: string
  state: string
  street: string
  postalCode: string
  place_id: string
}

export interface StoreLocatorComponentProps {
  confirmAddressModalImage: string
  safetyValidationProps: ISafetyValidationProps
  generateOTP: ({
    mapsAddress,
    valueAddressStreetNumber,
    addressRef
  }) => Promise<number>
}

export const StoreLocatorComponent = ({
  confirmAddressModalImage,
  safetyValidationProps,
  generateOTP
}: StoreLocatorComponentProps): React.ReactElement => {
  const router = useRouter()
  const { isLoggedIn } = useSession()
  const { isMobile } = useMobile()
  const [isLoadingAddressList, setIsLoadingAddressList] = useState(false)
  const [failModal, setFailModal] = useState(false)
  const [mapModal, setMapModal] = useState(false)
  const [errorAddress, setErrorAddress] = useState(false)
  const [streetNumberInput, setStreetNumberInput] = useState(false)
  const [address, setAddress] = useState('')
  const [valueAddressStreetNumber, setValueAddressStreetNumber] = useState('')
  const [errorMapMessage, setErrorMapMessage] = useState('')
  const [addressRef, setAddressRef] = useState('')
  const [addressComments, setAddressComments] = useState('')
  const [buttonStatus, setButtonStatus] = useState('')
  const [errorType, setErrorType] = useState('')
  const [verifiedSc, setSc] = useState('')
  const [userDirection, setUserDirection] = useState(false)
  const [failModalColorTitle, setFailModalColorTitle] = useState('')
  const [failModalButtonText, setFailModalButtonText] = useState('')
  const [failModalContent, setFailModalContent] = useState('')
  const [failModalTitle, setFailModalTitle] = useState('')
  const [titlePosition, setTitlePosition] =
    useState<TitleProps['textAlign']>('center')
  const [contentPosition, setContentPosition] =
    useState<TitleProps['textAlign']>('center')
  const [failModalEvent, setFailModalEvent] = useState('')
  const [addressToDelete, setAddressToDelete] = useState(null)
  const [confirmAddressButtonStatus, setConfirmAddressButtonStatus] =
    useState<ButtonStatus>('disabled')
  const [latMap, setLatMap] = useState(0)
  const [lngMap, setLngMap] = useState(0)
  const [addressList, setAddressList] = useState([])
  const [addressSelected, setAddressSelected] = useState(null)
  const [mapsAddress, setMapsAddress] = useState<AddressMaps | null>(null)
  const { data: orderFormData } = useOrderForm()
  const {
    handleAddressObject,
    data: updateData,
    isSuccess
  } = useUpdateAddress(router)
  const [directionModal, setDirectionModal] = useState(false)
  const [selectedManualAddres] = useState(false)
  const [regionList, setRegionList] = useState([])
  const [comunaList, setComunaList] = useState([])
  const [manualAddress, setManualAddress] = useState(null)
  const [dontAllowCloseSL, setDontAllowCloseSL] = useState(false)
  const [saveSalesChannel, setSaveSalesChannel] = useState(false)
  const [sameAddressOnClose, setSameAddressOnClose] = useState(false)
  const [hasProducts, setHasProducts] = useState(false)
  const [openModalConfirmChangeAddress, setOpenModalConfirmChangeAddress] =
    useState(false)
  const [confirmNewAddress, setConfirmNewAddress] = useState(false)
  const [AddressConfirmModal, setAddressConfirmModal] = useState(false) // State: address confirmation modal visibility
  const [EditAddressModal, setEditAddressModal] = useState(false) // State: edit address modal visibility
  const [geolocationErrorCode, setGeolocationErrorCode] = useState<
    number | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const getPreferredStreet = (fallbackName?: string, neighborhood?: string) => {
    const preferredFull = (
      fallbackName ||
      address ||
      manualAddress ||
      ''
    ).trim()
    const preferred = preferredFull.split(',')[0].trim()
    if (preferred.length > 0) return preferred
    return (neighborhood || '').trim()
  }

  const { regions: availableStores } = regions
  const { trigger } = useTrigerEvent()
  const {
    suggestions: { data },
    setValue
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'cl' }
      // in here we can filter by address type example:
      // types: ['address']
    },
    cache: false
  })

  const { saveLocalAddress, localOrderForm } = useLocalAddress()

  const EVENT_CATEGORY_DELIVERY_METHODS = 'interacciones en metodo de entrega'
  useEvents({
    eventType: 'storeLocator',
    callBack: ({
      detail: {
        show,
        toCC,
        dontAllowCloseSL,
        saveSalesChannel,
        sameAddressOnClose
      }
    }) => {
      setDirectionModal(show)
      if (toCC !== undefined) {
        trigger({
          eventType: 'storeLocator',
          data: { show: toCC }
        })
      }
      const localProducts = Cookies.get('addToCart') || '[]'
      setHasProducts(
        isValidArrayWithData(
          isLoggedIn ? orderFormData?.data?.items : JSON.parse(localProducts)
        )
      )
      // Coerce any value into a strict boolean using double negation (!!)
      setDontAllowCloseSL(!!dontAllowCloseSL)
      setSaveSalesChannel(!!saveSalesChannel)
      setSameAddressOnClose(!!sameAddressOnClose)
    }
  })

  const findSelectedStore = addressList.findIndex((addressSelect) => {
    return addressSelect.selected === true
  })

  const handleOnClick = (notClosing = false) => {
    const isNotClosing = typeof notClosing !== 'boolean' ? false : notClosing
    setConfirmAddressButtonStatus('disabled')
    if (saveSalesChannel && !isNotClosing) {
      updateSalesChannel(Number(orderFormData?.data?.salesChannel))
    }
    if (sameAddressOnClose && !isNotClosing) {
      confirmUserAddress(true)
    }
    trigger({
      eventType: 'storeLocator',
      data: { show: !directionModal, dontAllowCloseSL, saveSalesChannel }
    })
  }

  const handleSelect = async ({
    description,
    refreshMap = true
  }: {
    description: string
    refreshMap?: boolean
  }) => {
    const results = await getGeocode({ address: description })
    const formatedDirection = addressFormat(results)
    if (!formatedDirection.street || formatedDirection.street === '') {
      formatedDirection.street = getPreferredStreet(
        description,
        formatedDirection.neighborhood
      )
    }
    const { lat, lng } = await getLatLng(results[0])

    if (formatedDirection.number.length === 0) {
      formatedDirection.number =
        valueAddressStreetNumber && valueAddressStreetNumber?.length > 0
          ? valueAddressStreetNumber
          : 's/n'
      setStreetNumberInput(true)
    }
    if (!formatedDirection.street || formatedDirection.street == '') {
      formatedDirection.street = getPreferredStreet(
        description,
        formatedDirection.neighborhood
      )
    }
    setMapsAddress(formatedDirection)
    const response = await postCoordsValidate({ data: [lng, lat] })
    if (!response.data.error) {
      setSc(response.data.sc.toString())
      setAddress(description)
      setLatMap(lat)
      setLngMap(lng)
      if (refreshMap) {
        handleOnClick()
        setMapModal(!mapModal)
      }
    } else {
      setFailModal(!failModal)
      refreshMap ? handleOnClick() : setMapModal(!mapModal)
    }

    return { error: response.data.error }
  }

  const handleSelectManualAddress = async () => {
    datalayerSimpleEvent({
      event: 'evento_interactivo',
      eventAction: 'dirección manual',
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'Ingresar dirección manualmente'
    })
    setRegionList(
      availableStores.map((store) => {
        return store.region
      })
    )
    setEditAddressModal(true)
    setMapModal(!mapModal)
    handleOnClick(true)
  }

  const regionSelector = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === 'Seleccionar región') return setComunaList([])

    const filterStore = availableStores?.filter((elem) => {
      return elem.region === event.target.value
    })[0]
    return setComunaList(
      filterStore.comunas.map((comuna) => {
        return comuna.name
      })
    )
  }

  const setNewLocation = (lat: number, lng: number) => {
    setLatMap(lat)
    setLngMap(lng)
  }

  const errorModal = () => {
    setFailModal(!failModal)
    setFailModalColorTitle(getGlobalStyle('--color-base-black'))
    setFailModalTitle('Dirección sin cobertura de despacho')
    setContentPosition('center')
    setFailModalButtonText('Cambiar dirección')
    setFailModalContent(
      'No podemos despachar a tu dirección. Si quieres continuar comprando cambia tu dirección.'
    )
    setAddress('')
    setValue('')
  }

  const mapCloseModal = (withTag = false) => {
    withTag &&
      eventClick({
        eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
        eventLabel: 'modal cerrar'
      })
    setMapModal(false)
    setButtonStatus('')
    setManualAddress('')
    setMapsAddress(null)
    setAddress('')
    setAddressRef('')
    setAddressComments('')
    setStreetNumberInput(false)
    setValueAddressStreetNumber('')
    if (errorAddress) {
      setErrorAddress(!errorAddress)
    }
  }

  const mapBackModal = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'modal atras'
    })
    setAddress('')
    setValue('')
    setButtonStatus('')
    setManualAddress('')
    setMapsAddress(null)
    setAddressRef('')
    setAddressComments('')
    setMapModal(false)
    setStreetNumberInput(false)
    setValueAddressStreetNumber('')
    setDirectionModal(true)
  }

  const mapErrorAddress = (error: string, type: string) => {
    if (!error) {
      setErrorAddress(false)
    }

    if (error) {
      setButtonStatus('disabled')
      setErrorType(type)
      setErrorMapMessage(error)
      setErrorAddress(true)
    }
  }

  const handleNewAddress = async (obj: NewAddressData) => {
    if (isLoggedIn) {
      const { city, country, neighborhood, number, state, street } = obj
      const responseNewAddress = await postNewAddress(obj)
      const isError = responseNewAddress?.data['error']
      if (typeof isError === 'boolean' && !isError) {
        handleAddressObject({
          addressId: responseNewAddress.data['data'].addressName,
          addressType: responseNewAddress.data['data'].addressType,
          city: city,
          complement: addressRef,
          country: country,
          geoCoordinates: [lngMap, latMap],
          neighborhood: neighborhood,
          number: number.length === 0 ? valueAddressStreetNumber : number,
          reference: addressComments,
          state: state,
          street:
            street.length === 0
              ? getPreferredStreet(undefined, neighborhood)
              : street,
          salesChannel: verifiedSc
        })
        return
      }
      setButtonStatus('')
      setAddressConfirmModal(false)
      setDirectionModal(false)
      safetyValidationProps.handleCloseModal()
      setOpenModalConfirmChangeAddress(false)

      trigger({
        eventType: 'addressChanged',
        data: {
          show: true,
          isError,
          message: 'No pudimos agregar tu dirección, intenta nuevamente.'
        }
      })
      return
    }
    setButtonStatus('initial')
    saveLocalAddress(
      {
        data: { ...obj, addressType: 'residential' },
        responseData: {
          salesChannel: verifiedSc,
          reference: addressComments,
          number: valueAddressStreetNumber,
          geoCoordinates: [lngMap, latMap],
          complement: addressRef
        }
      },
      router
    )
    setMapModal(false)
  }

  const handleGenerateOTP = async () => {
    const status = await generateOTP({
      mapsAddress,
      valueAddressStreetNumber,
      addressRef
    })

    if (status === 201) {
      setMapModal(false)
    }
    setButtonStatus('')
  }

  const setAddressFromMap = async (): Promise<void> => {
    safetyValidationProps.handleLoadingButton(true)
    const { place_id, number, ...propsMapsAddress } = mapsAddress

    const {
      data: {
        userProfileId,
        clientProfileData: { firstName, lastName }
      }
    } = orderFormData || localOrderForm

    handleNewAddress({
      ...propsMapsAddress,
      complement: addressRef,
      geoCoordinate: [lngMap, latMap],
      number: number.length === 0 ? valueAddressStreetNumber : number,
      placeId: place_id,
      receiverName: `${firstName} ${lastName}`,
      reference: addressComments,
      userId: userProfileId
    })
  }

  const handleShowFailModal = () => {
    mapCloseModal()
    setFailModal(!failModal)
    setFailModalColorTitle(getGlobalStyle('--color-base-black'))
    setFailModalTitle('Dirección sin cobertura de despacho')
    setContentPosition('center')
    setFailModalButtonText('Cambiar dirección')
    setFailModalContent(
      'No podemos despachar a tu dirección. Si quieres continuar comprando cambia tu dirección.'
    )
    setButtonStatus('')
  }

  const handleValidateCoords = async () => {
    setButtonStatus('loading')
    datalayerSimpleEvent({
      event: 'evento_interactivo',
      eventAction: 'dirección manual',
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'Confirmar dirección'
    })
    if (!navigator?.onLine)
      mapErrorAddress(
        'Lo sentimos, no tienes conexión por favor intenta de nuevo.',
        'error'
      )
    else {
      if (buttonStatus === 'loading') return
      setButtonStatus('loading')

      const addressVal = `${mapsAddress?.street}, ${
        mapsAddress?.number?.length === 0 || mapsAddress?.number === 's/n'
          ? valueAddressStreetNumber
          : mapsAddress?.number
      }, ${mapsAddress?.neighborhood}, ${mapsAddress?.state}, ${mapsAddress?.country}`

      const geoCode = await getGeocode({ address: addressVal })
      const { lng, lat } = await getLatLng(geoCode[0])
      const dataLngLat = await postCoordsValidate({ data: [lng, lat] })

      if (dataLngLat?.data?.error) {
        handleShowFailModal()
        return
      }

      if (isLoggedIn) {
        // Logged user flow: send the client to a validation (OTP) screen
        handleGenerateOTP()
        return
      }

      setAddressFromMap()
    }
  }

  const buttonAddressMap = async () => {
    const response = await postCoordsValidate({
      data: [lngMap, latMap]
    })
    if (!response.data.error) {
      // New flow to validate OTP
      if (isLoggedIn) {
        if (!safetyValidationProps?.safetyValidationModal) {
          // validate coords and generate OTP
          return handleValidateCoords()
        } else if (hasProducts && !localOrderForm?.data?.selectedAddresses) {
          // show confirm modal when change address after validate OTP
          setAddressConfirmModal(false)
          setDirectionModal(false)
          setConfirmNewAddress(true)
          safetyValidationProps.handleCloseModal()
          return setOpenModalConfirmChangeAddress(true)
        }
        // If there are no products, after OTP validation the address will be set silently
        return setAddressFromMap()
      }

      // Guest flow keeps the original coordinate validation logic
      return handleValidateCoords()
    }
    setDirectionModal(false)
    setAddressConfirmModal(false)
    return setFailModal(!failModal)
  }

  useEffect(() => {
    if (isSuccess) {
      setOpenModalConfirmChangeAddress(false)
      const domain = getCurrentDomain()
      Cookies.set('showAddressChangedTooltip', 'true', {
        domain,
        path: '/'
      })
    }
  }, [isSuccess])

  useEffect(() => {
    if (updateData) {
      if (!userDirection) {
        if (updateData?.data?.error) {
          mapErrorAddress(
            'Lo sentimos, tuvimos un error al cargar tu información por favor intenta de nuevo.',
            'error'
          )
          setButtonStatus('')
        } else {
          setButtonStatus('')
          mapCloseModal()
        }
      } else {
        if (updateData?.data?.error) {
          setConfirmAddressButtonStatus('disabled')
          throw new Error('Error en el cambio de dirección')
        } else {
          trigger({ eventType: 'storeLocator', data: { show: false } })
          setConfirmAddressButtonStatus('disabled')
        }
      }
    }
  }, [updateData?.data?.error])

  const getAddresId = () => {
    if (orderFormData) {
      return orderFormData?.data?.selectedAddresses?.addressId || ''
    }
    return ''
  }

  useEffect(() => {
    if (!openModalConfirmChangeAddress && !addressToDelete) {
      ;(async function () {
        try {
          setIsLoadingAddressList(true)

          const userAddress = getAddresId()
          setValue('')
          const response = isLoggedIn
            ? await getAddress()
            : { data: localOrderForm.data.availableAddresses }

          if (response?.data?.length === 0) {
            setAddressList([])
          }
          const addressListFormated = []
          response?.data?.forEach((element) => {
            addressListFormated.push({
              addressName: element.addressName,
              addressType: element.addressType,
              city: element.city,
              complement: element.complement,
              country: element.country,
              geoCoordinate: element.geoCoordinate,
              id: element.id,
              neighborhood: element.neighborhood,
              number: element.number,
              postalCode: element.postalCode,
              receiverName: element.receiverName,
              reference: element.reference,
              state: element.state,
              street: element.street,
              selected: userAddress !== '' ? userAddress === element.id : false
            })
          })
          for (let index = 0; index < addressListFormated.length; index++) {
            const addressIterated = addressListFormated[index]
            if (addressIterated.selected)
              addressSelector(addressIterated, index)
          }
          setAddressList(addressListFormated)
        } catch {
          setAddressList([])
        } finally {
          setIsLoadingAddressList(false)
        }
      })()
    }
    return () => {
      setAddressList([])
    }
  }, [directionModal, openModalConfirmChangeAddress])

  const handleClearInputAddress = () => {
    setAddress('')
    setValue('')
  }

  const addressOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAddress(event.target.value)
    if (event.target.value.length > 2) setValue(event.target.value.toString())
    if (event.target.value.length == 3) {
      eventClick({
        eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
        eventAction: 'carga modal',
        'event-label': 'digitar nueva direccion'
      })
    }
    if (event.target.value.length === 0) {
      setAddress('')
      setValue('')
    }
  }

  const mapAddressComentsOnClearInput = (
    _,
    ref: RefObject<HTMLInputElement>
  ) => {
    ;(ref.current.children[0].children[0] as HTMLInputElement).value = ''
    setAddressComments('')
  }

  const mapAddressComentsOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAddressComments(event.target.value)
  }

  // note ref, _
  const mapAddressRefOnClearInput = (_, ref: RefObject<HTMLInputElement>) => {
    ;(ref.current.children[0].children[0] as HTMLInputElement).value = ''
    setAddressRef('')
  }

  const mapAddressOnClearInput = (_, ref: RefObject<HTMLInputElement>) => {
    ;(ref.current.children[0].children[0] as HTMLInputElement).value = ''
    setManualAddress('')
  }

  const mapAddressRefOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAddressRef(event.target.value)
  }

  const mapAddressOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setManualAddress(event.target.value)
  }

  const mapAddressStreetNumberOnClearInput = (
    _,
    ref: RefObject<HTMLInputElement>
  ) => {
    ;(ref.current.children[0].children[0] as HTMLInputElement).value = ''
    setValueAddressStreetNumber('')
  }

  const mapAddressStreetNumberOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValueAddressStreetNumber(event?.target?.value)
    setMapsAddress({
      ...mapsAddress,
      number: event?.target?.value
    })
  }

  const buttonLogic = () => {
    if (buttonStatus === 'disabled') return 'disabled'
    if (buttonStatus === 'loading') return 'loading'
    if (buttonStatus === '') return 'initial'
    // if (mapsAddress?.number?.length > 0) return 'initial'
    // if (
    //   mapsAddress?.number?.length === 0 &&
    //   valueAddressStreetNumber?.length > 0
    // )
    //   return 'initial'
    // if (availableStores && manualAddress?.length > 0) return 'initial'

    return 'disabled'
  }

  const addressSelector = (item: object, index: number) => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'modal seleccion de radio button'
    })
    if (findSelectedStore !== -1) {
      addressList[findSelectedStore].selected = false
      addressList[index].selected = true
      setAddressList([...addressList])
    } else {
      if (addressList[index]) {
        addressList[index].selected = true
        setAddressList([...addressList])
      }
    }
    setAddressSelected(item)
    setConfirmAddressButtonStatus('initial')
  }

  const changeDeliveryMethod = async () => {
    datalayerSimpleEvent({
      event: 'evento_interactivo',
      eventAction: 'dirección manual',
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'cambiar a retiro en tienda'
    })
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'modal boton cambiar retiro en tienda'
    })
    trigger({
      eventType: 'deliverySelector',
      data: { toStore: true, dontAllowCloseSL }
    })
    setDirectionModal(false)
  }

  const markingConfirmButton = async () => {
    datalayerSimpleEvent({
      event: 'evento_interactivo',
      eventAction: 'dirección manual',
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'Confirmar dirección'
    })
  }

  const confirmUserAddress = async (closeSL?: boolean) => {
    const { data } = orderFormData || localOrderForm
    const isSameAddress =
      sameAddressOnClose &&
      (data?.selectedAddresses?.addressId === addressSelected?.id ||
        closeSL === true)
    const baseGeoCoodinate = isSameAddress
      ? data?.selectedAddresses?.geoCoordinates
      : addressSelected?.geoCoordinate

    // Show modal to confirm change only when the newly selected address differs from the current one
    if (
      hasProducts &&
      data?.selectedAddresses?.addressId !== addressSelected?.id
    ) {
      setHasProducts(false)
      setDirectionModal(false)
      setOpenModalConfirmChangeAddress(true)
      return
    }
    setButtonStatus('loading')
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'boton confirmar direccion guardada'
    })
    setConfirmAddressButtonStatus('loading')
    setUserDirection(true)

    const response = await postCoordsValidate({
      data: baseGeoCoodinate
    })

    if (!response.data.error) {
      if (isLoggedIn) {
        if (isSameAddress) {
          handleAddressObject({
            ...data?.selectedAddresses,
            salesChannel: response?.data?.sc?.toString(),
            geoCoordinates: data?.selectedAddresses?.geoCoordinates
          })
        } else {
          handleAddressObject({
            ...addressSelected,
            salesChannel: response?.data?.sc?.toString(),
            addressId: addressSelected?.id,
            geoCoordinates: addressSelected?.geoCoordinate
          })
        }
      } else {
        saveLocalAddress(
          {
            data: {
              ...addressSelected,
              salesChannel: response?.data?.sc?.toString(),
              geoCoordinates:
                addressSelected?.geoCoordinate ||
                data?.selectedAddresses?.geoCoordinates
            },
            responseData: {
              salesChannel: response?.data?.sc
            }
          },
          router
        )
      }
    } else {
      setOpenModalConfirmChangeAddress(false)
      setConfirmAddressButtonStatus('initial')
      handleShowFailModal()
    }
  }

  const deleteAddress = async () => {
    if (addressToDelete) {
      setAddressList(
        addressList.filter((i) => {
          return i?.id !== addressToDelete?.id
        })
      )
      setFailModal(!failModal)
      handleOnClick()
    }
  }

  const openDeleteAddressModal = (item: object) => {
    setAddressToDelete(item)
    setFailModal(!failModal)
    setFailModalColorTitle(getGlobalStyle('--color-primary-red'))
    setFailModalTitle('¿Estás seguro que quieres eliminar esa dirección?')
    setFailModalButtonText('Eliminar')
    setTitlePosition('center')
    setFailModalContent(
      'Si lo eliminas, tendras que volver a agregarla para usarla de nuevo.'
    )
    setFailModalEvent('deleteAddress')
    handleOnClick()
  }

  const handleActionModal = () => {
    if (failModalEvent === 'deleteAddress') deleteAddress()
    setAddress('')
    setValue('')
    setFailModal(!failModal)
    handleOnClick()
  }

  const handleKeepAddress = () => {
    mapCloseModal()
    setFailModal(false)
  }

  const confirmAddressModal = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'abrir nuevo modal personalizado'
    })

    setMapModal(false)
    setAddressConfirmModal(true)
    handleOnClick()
  }

  const closeAddressConfirmModal = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'cerrar nuevo modal personalizado'
    })

    setAddressConfirmModal(false)
    setMapModal(false)
    setButtonStatus('')
    setManualAddress('')
    setMapsAddress(null)
    setAddress('')
    setAddressRef('')
    setAddressComments('')
    setStreetNumberInput(false)
    setValueAddressStreetNumber('')
    setDirectionModal(false)
    if (errorAddress) {
      setErrorAddress(!errorAddress)
    }
  }

  const addressConfirmModalBack = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'modal atras'
    })
    setMapModal(!mapModal)
    setAddressConfirmModal(false)
  }

  const BackEditAddressModal = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'modal atras'
    })
    setEditAddressModal(false)
    setMapModal(true)
    handleOnClick(true)
  }

  const closeEditModal = () => {
    setEditAddressModal(false)
    setAddressConfirmModal(false)
    handleOnClick(false)
    setMapModal(false)
    setButtonStatus('')
    setManualAddress('')
    setMapsAddress(null)
    setAddress('')
    setAddressRef('')
    setAddressComments('')
    setStreetNumberInput(false)
    setValueAddressStreetNumber('')
    setDirectionModal(false)
    if (errorAddress) {
      setErrorAddress(!errorAddress)
    }
  }

  // Function to validate and format an address string
  const validateAndFormatAddress = (addressString: string): string => {
    if (!addressString) return ''
    // Obtener el número desde mapsAddress si existe
    const addressNumber = mapsAddress?.number || ''
    // Buscar si el número está presente en el addressString
    const hasNumber = addressNumber && addressString.includes(addressNumber)

    let processed = addressString

    // Si no tiene número, agregar s/n como placeholder
    if (!hasNumber) {
      const parts = addressString.split(',')
      const baseStreet = parts[0]?.trim() || ''
      const rest = parts
        .slice(1)
        .map((p) => {
          return p.trim()
        })
        .filter((p) => {
          return p.length > 0
        })
      processed = [baseStreet + ' s/n', ...rest].join(', ')
    }
    // Insertar addressRef después del número (o s/n) y antes del resto
    if (addressRef && addressRef.trim().length > 0) {
      const parts = processed.split(',').map((p) => {
        return p.trim()
      })
      if (parts.length > 0) {
        const first = parts[0]
        // Buscar si el número está en la primera parte
        const numberMatch = addressNumber && first.includes(addressNumber)
        const snMatch = /s\/n$/.test(first)
        if (numberMatch || snMatch) {
          const alreadyHasRef = parts[1] && parts[1] === addressRef.trim()
          if (!alreadyHasRef) {
            parts.splice(1, 0, addressRef.trim())
          }
        } else {
          parts[0] = first + ' s/n'
          parts.splice(1, 0, addressRef.trim())
        }
        processed = parts.join(', ')
      }
    }
    return processed
  }

  // Helper to construct the address and fetch geocoordinates.
  // If addressObj is not provided it will use current mapsAddress state.
  // Updates latMap / lngMap and returns { lat, lng } or null on failure.
  const getCoordsFromAddress = async (
    addressObj?: Partial<
      Pick<
        AddressMaps,
        'street' | 'number' | 'neighborhood' | 'state' | 'country' | 'city'
      > & { apart?: string }
    >
  ): Promise<{ lat: number; lng: number } | null> => {
    setIsLoading(true)
    try {
      // 1. Merge source: current state + incoming override
      const base = {
        ...(mapsAddress || {}),
        ...(addressObj || {})
      } as Partial<AddressMaps>
      if (!base.street || base.street === '') {
        base.street = getPreferredStreet(undefined, base.neighborhood)
      }
      if (addressObj?.apart && addressObj?.apart?.length > 0) {
        setAddressRef(addressObj?.apart)
      }
      setMapsAddress(base as AddressMaps)

      // Guard clause: require at least street, state and country for reliable geocoding.
      if (!base.street || !base.state || !base.country) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            '[StoreLocator] getCoordsFromAddress faltan campos mínimos',
            base
          )
        }
        return null
      }

      // 3. Build geocode string (filter empty parts)
      const parts = [
        base.street,
        base.number,
        base.neighborhood,
        base.city, // opcional
        base.state,
        base.country
      ]
        .filter(Boolean)
        .map((p) => {
          return typeof p === 'string' ? p.trim() : p
        })
        .filter((p) => {
          return p && (p as string).length > 0
        })

      let addressString = parts.join(', ')
      addressString = addressString.replace(/,\s*(?=\d)/, ' ')
      // 4. Geocode
      const geoCode = await getGeocode({ address: addressString })
      if (!geoCode?.length) {
        mapErrorAddress(
          'No encontramos la dirección. Ajusta los datos e intenta nuevamente.',
          'error'
        )
        setIsLoading(false)
        return null
      }

      const { lat, lng } = await getLatLng(geoCode[0])
      const city =
        geoCode[0]?.address_components?.find((component) => {
          return component.types.includes('administrative_area_level_2')
        })?.long_name ?? ''

      // update city from google maps
      setMapsAddress({
        ...base,
        city
      } as AddressMaps)

      // 6. Update states (coordinates + address object + visible string)
      const response = await postCoordsValidate({ data: [lng, lat] })
      setIsLoading(false)
      if (!response.data.error) {
        setEditAddressModal(false)
        setLatMap(lat)
        setLngMap(lng)
        setMapModal(true)
      } else {
        setDirectionModal(false)
        setEditAddressModal(false)
        setFailModal(!failModal)
      }
      // setMapsAddress({
      //   ...(mapsAddress || {}),
      //   ...formatted,
      //   number: formatted.number
      // })
      // Visible address: prefer formatted_address, otherwise fallback to reconstructed string
      setAddress(addressString)

      // Clear previous errors if any
      mapErrorAddress('', '')

      return { lat, lng }
    } catch (error) {
      mapErrorAddress(
        'Ocurrió un error al buscar la dirección. Intenta nuevamente.',
        'error'
      )
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[StoreLocator] getCoordsFromAddress error:', error)
      }
      return null
    }
  }

  const getGeolocation = async () => {
    // Solicita la geolocalización del navegador
    if (!navigator?.geolocation) {
      // Geolocalización no soportada
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        setLatMap(latitude)
        setLngMap(longitude)
        // Solo valida las coordenadas si la geolocalización fue exitosa
        const response = await postCoordsValidate({
          data: [longitude, latitude]
        })
        if (!response.data.error) {
          getGeocode({ location: { lat: latitude, lng: longitude } })
            .then((results) => {
              if (results && results.length > 0) {
                setAddress(results[0].formatted_address || '')
                const formatted = addressFormat(results)
                if (!formatted.street || formatted.street === '') {
                  formatted.street = getPreferredStreet(
                    undefined,
                    formatted.neighborhood
                  )
                }
                setMapsAddress(formatted)
                setMapModal(true)
                setEditAddressModal(false)
              }
            })
            .catch(() => {
              // Error reverse geocoding
            })
        } else {
          setDirectionModal(false)
          setFailModal(true)
        }
      },
      (error) => {
        if (error.code === 1) {
          setGeolocationErrorCode(1) // Permiso denegado
          return
        }
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    )
  }

  const resetGeolocationError = () => {
    setGeolocationErrorCode(0)
  }

  const clickFailModal = () => {
    eventClick({
      eventCategory: EVENT_CATEGORY_DELIVERY_METHODS,
      eventLabel: 'Fail modal'
    })

    closeEditModal()
    setDirectionModal(true)
  }

  const CloseFailModal = () => {
    setMapModal(false)
    setButtonStatus('')
    setManualAddress('')
    setMapsAddress(null)
    setAddress('')
    setAddressRef('')
    setAddressComments('')
    setStreetNumberInput(false)
    setValueAddressStreetNumber('')
    setFailModal(!failModal)
  }

  const { data: dataCFImg } = useContentful({
    environment: 'master',
    id_contentful: 'contentful-icon-address-cake',
    options: {
      'sys.id': ADDRESS_ICONS.imgCake
    },
    type: 'assets'
  })

  const { data: dataMelocotonImg } = useContentful({
    environment: 'master',
    id_contentful: 'contentful-icon-address-melocoton',
    options: {
      'sys.id': ADDRESS_ICONS.imgMelocoton
    },
    type: 'assets'
  })

  const img = dataCFImg?.['items']?.[0]?.fields?.file?.url
  const imgAlt = 'cheesecake'
  const urlMelocotonImage = dataMelocotonImg?.['items']?.[0]?.fields?.file?.url
  const altMelocotonImage = 'melocoton'

  return (
    <>
      <ModalConfirmChangeAddress
        confirmAddressModalImage={confirmAddressModalImage}
        confirmNewAddress={confirmNewAddress}
        confirmUserAddress={confirmUserAddress}
        isMobile={isMobile}
        openModalConfirmChangeAddress={openModalConfirmChangeAddress}
        setAddressFromMap={setAddressFromMap}
        setOpenModalConfirmChangeAddress={setOpenModalConfirmChangeAddress}
      />
      <StoreLocatorV2
        addressConfirmModal={AddressConfirmModal}
        addressList={
          address
            ? data.map((addressData) => {
                return {
                  name: addressData.structured_formatting?.main_text,
                  city: addressData.structured_formatting?.secondary_text,
                  activeMap: false,
                  failModal: false,
                  description: addressData?.description
                }
              })
            : []
        }
        addressOnChangeSearch={addressOnChangeSearch}
        addressOnClearInput={handleClearInputAddress}
        addressSelector={addressSelector}
        backEditAddressModal={BackEditAddressModal}
        buttonAddressMap={buttonAddressMap}
        buttonStatus={buttonLogic()}
        changeDeliveryMethod={changeDeliveryMethod}
        clickFailModal={clickFailModal}
        closeAddressConfirmModal={closeAddressConfirmModal}
        closeEditModal={closeEditModal}
        closeFailModal={CloseFailModal}
        comunaList={comunaList}
        confirmAddressButtonStatus={confirmAddressButtonStatus}
        confirmAddressModal={confirmAddressModal}
        confirmEditAddressModal={getCoordsFromAddress}
        confirmModalBack={addressConfirmModalBack}
        confirmUserAddress={confirmUserAddress}
        contentPosition={contentPosition}
        deleteAddress={openDeleteAddressModal}
        directionModal={directionModal}
        dontAllowCloseSL={dontAllowCloseSL}
        editAddressModal={EditAddressModal}
        errorAddress={errorAddress}
        errorAddressType={errorType}
        errorMapMessage={errorMapMessage}
        errorModal={errorModal}
        failModal={failModal}
        failModalButtonText={failModalButtonText}
        failModalColorTitle={failModalColorTitle}
        failModalContent={failModalContent}
        failModalEvent={handleActionModal}
        failModalTitle={failModalTitle}
        formatAddress={validateAndFormatAddress}
        geolocationErrorCode={geolocationErrorCode}
        getGeolocalization={getGeolocation}
        handleKeepAddress={handleKeepAddress}
        handleOnClick={handleOnClick}
        handleSelectManualAddress={handleSelectManualAddress}
        image={img}
        imageAlt={imgAlt}
        infoAltImage={altMelocotonImage}
        infoUrlImage={urlMelocotonImage}
        isLoading={isLoading}
        isLoadingAddressList={isLoadingAddressList}
        mapAddress={mapsAddress}
        mapAddressCommentsOnChangeSearch={mapAddressComentsOnChangeSearch}
        mapAddressCommentsOnClearInput={mapAddressComentsOnClearInput}
        mapAddressOnChangeSearch={mapAddressOnChangeSearch}
        mapAddressOnClearInput={mapAddressOnClearInput}
        mapAddressRefOnChangeSearch={mapAddressRefOnChangeSearch}
        mapAddressRefOnClearInput={mapAddressRefOnClearInput}
        mapAddressStreetNumberOnChangeSearch={
          mapAddressStreetNumberOnChangeSearch
        }
        mapAddressStreetNumberOnClearInput={mapAddressStreetNumberOnClearInput}
        mapBackModal={mapBackModal}
        mapCloseModal={() => {
          mapCloseModal(true)
        }}
        mapModal={mapModal}
        markingConfirmButton={markingConfirmButton}
        openEditAddressModal={handleSelectManualAddress}
        regionList={regionList}
        regionSelector={regionSelector}
        resetGeolocationError={resetGeolocationError}
        safetyValidationProps={safetyValidationProps}
        selectedAddress={address}
        selectedManualAddres={selectedManualAddres}
        streetNumberInput={streetNumberInput}
        titlePosition={titlePosition}
        userAddressList={addressList}
        validateAddress={handleSelect}
        valueAddressComments={addressComments}
        valueAddressRef={addressRef}
        valueAddressStreetNumber={valueAddressStreetNumber}
        valueManualAddress={manualAddress}
        valueSearch={address}
      >
        <Map
          coordsError={mapErrorAddress}
          location={{ latMap, lngMap }}
          setButtonStatus={setButtonStatus}
          setNewLocation={setNewLocation}
        />
      </StoreLocatorV2>
    </>
  )
}
